import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import { History } from "lucide-react";
import DashboardShell from "../../components/layout/DashboardShell";
import { Button, Input, Select, TextArea } from "../../components/common/Primitives";
import PitchHealthMeter from "../../components/startup/PitchHealthMeter";
import SnapshotHistoryDrawer from "../../components/startup/SnapshotHistoryDrawer";
import PitchPolish from "../../components/startup/PitchPolish";
import FundingRoundsSection from "../../components/startup/FundingRoundsSection";
import FieldTip from "../../components/common/FieldTip";
import AutoSaveIndicator from "../../components/common/AutoSaveIndicator";
import useAutoSave from "../../lib/useAutoSave";
import api, { formatApiError } from "../../lib/api";
import useFocusHighlight from "../../lib/useFocusHighlight";

const fundingTypes = ["Equity", "Debt", "Hybrid", "Convertible"];

export default function StartupFundingProfile() {
  useFocusHighlight();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  const load = () => api.get("/startup/me").then((r) => { setForm(r.data.funding || {}); setLoading(false); });
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setNum = (k) => (e) => setForm({ ...form, [k]: e.target.value ? Number(e.target.value) : undefined });

  const doSave = async (silent = false) => {
    await api.put("/startup/funding-profile", form);
    if (!silent) toast.success("Funding profile saved.");
  };

  const { status: saveStatus, lastSavedAt } = useAutoSave(
    form,
    () => doSave(true),
    20000,
    !loading
  );

  const save = async (e) => {
    e.preventDefault();
    try { await doSave(false); } catch (err) { toast.error(formatApiError(err)); }
  };

  if (loading) return <DashboardShell title="Funding Profile"><div className="text-ink-muted">Loading…</div></DashboardShell>;

  const isDebt = form.funding_type === "Debt";

  return (
    <DashboardShell title="Funding Profile" subtitle="STARTUP · CAPITAL REQUIREMENT"
      actions={
        <div className="flex items-center gap-3">
          <button
            onClick={() => setHistoryOpen(true)}
            className="text-xs px-3 py-1.5 rounded-md border border-navy/15 text-ink hover:bg-navy/[0.04] inline-flex items-center gap-1.5"
            data-testid="open-snapshot-history-funding"
          >
            <History size={12} /> History
          </button>
          <AutoSaveIndicator status={saveStatus} lastSavedAt={lastSavedAt} />
        </div>
      }
    >
      <Toaster richColors position="top-right" />
      <SnapshotHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        kind="funding"
        onReverted={() => load()}
      />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl">
        <form onSubmit={save} className="lg:col-span-2 space-y-6" data-testid="funding-form">
          <section className="border border-white/10 bg-white/70 backdrop-blur p-6 rounded-lg">
            <div className="overline mb-4">Business & Model</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <TextArea label="Problem Statement" value={form.problem_statement || ""} onChange={set("problem_statement")} testId="fp-problem" />
                <div className="flex items-start justify-between gap-3 mt-1">
                  <div className="text-[11px] text-ink-muted flex items-center gap-1">
                    <FieldTip
                      why="Investors read this first. If the problem isn't clear, everything else gets skipped."
                      example="'Indian SMEs spend 40+ hours/month on GST compliance — most still do it manually.'"
                      impact="Adds 8pt to your Pitch Health."
                    />
                    <span>Two crisp sentences work best.</span>
                  </div>
                  <PitchPolish
                    field="problem_statement"
                    text={form.problem_statement || ""}
                    onAccept={(rewrite) => setForm((f) => ({ ...f, problem_statement: rewrite }))}
                  />
                </div>
              </div>
              <div className="md:col-span-2">
                <TextArea label="Solution" value={form.solution || ""} onChange={set("solution")} testId="fp-solution" />
                <div className="flex items-start justify-between gap-3 mt-1">
                  <div className="text-[11px] text-ink-muted flex items-center gap-1">
                    <FieldTip
                      why="How you solve the problem, in plain language."
                      example="'AI-powered GST filing that reduces the workflow to 45 minutes/month.'"
                      impact="Adds 8pt to your Pitch Health."
                    />
                  </div>
                  <PitchPolish
                    field="solution"
                    text={form.solution || ""}
                    onAccept={(rewrite) => setForm((f) => ({ ...f, solution: rewrite }))}
                  />
                </div>
              </div>
              <Input label="Product / Service" value={form.product_service || ""} onChange={set("product_service")} testId="fp-product" />
              <Input label="Business Model" value={form.business_model || ""} onChange={set("business_model")} testId="fp-business-model" />
              <Input label="Revenue Model" value={form.revenue_model || ""} onChange={set("revenue_model")} testId="fp-revenue-model" />
              <Input label="Target Customers" value={form.target_customers || ""} onChange={set("target_customers")} testId="fp-target" />
              <Input label="Market Size" value={form.market_size || ""} onChange={set("market_size")} testId="fp-market" />
              <div>
                <Input label="Traction" value={form.traction || ""} onChange={set("traction")} testId="fp-traction" />
                <div className="flex items-start justify-between gap-3 mt-1">
                  <div className="text-[11px] text-ink-muted flex items-center gap-1">
                    <FieldTip
                      why="Traction is the #1 field institutional investors filter on."
                      example="'₹2Cr ARR · 480 paying SMBs · 8% MoM growth'"
                      impact="Fills your top-3 quick-win slot."
                    />
                  </div>
                  <PitchPolish
                    field="traction"
                    text={form.traction || ""}
                    onAccept={(rewrite) => setForm((f) => ({ ...f, traction: rewrite }))}
                  />
                </div>
              </div>
            </div>
          </section>

          <section className="border border-white/10 bg-white/70 backdrop-blur p-6 rounded-lg">
            <div className="overline mb-4">Financials</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input label="Monthly Revenue (₹)" type="number" value={form.monthly_revenue || ""} onChange={setNum("monthly_revenue")} testId="fp-mrr" />
              <Input label="Annual Revenue (₹)" type="number" value={form.annual_revenue || ""} onChange={setNum("annual_revenue")} testId="fp-arev" />
              <Input label="ARR (₹)" type="number" value={form.arr || ""} onChange={setNum("arr")} testId="fp-arr" />
              <Input label="EBITDA Status" value={form.ebitda_status || ""} onChange={set("ebitda_status")} testId="fp-ebitda" />
              <Input label="Gross Margin (%)" type="number" value={form.gross_margin || ""} onChange={setNum("gross_margin")} testId="fp-gm" />
              <Input label="Burn Rate (₹/month)" type="number" value={form.burn_rate || ""} onChange={setNum("burn_rate")} testId="fp-burn" />
              <Input label="Runway (months)" type="number" value={form.runway_months || ""} onChange={setNum("runway_months")} testId="fp-runway" />
            </div>
          </section>

          <section className="border border-white/10 bg-white/70 backdrop-blur p-6 rounded-lg">
            <div className="overline mb-4">Funding Requirement</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select label="Funding Type" value={form.funding_type || ""} onChange={set("funding_type")} testId="fp-type">
                <option value="">Select type</option>
                {fundingTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </Select>
              <div>
                <div data-focus="funding-ask">
                  <Input label="Amount Required (₹)" type="number" value={form.amount_required || ""} onChange={setNum("amount_required")} testId="fp-amount" />
                </div>
                <div className="text-[11px] text-ink-muted mt-1 flex items-center gap-1">
                  <FieldTip
                    why="92% of investors filter the Directory by ticket size."
                    example="₹5,00,00,000 for a Series A round"
                    impact="Missing this pushes you out of ticket-based search results."
                  />
                </div>
              </div>
              <Input label="Valuation Expectation (₹)" type="number" value={form.valuation_expectation || ""} onChange={setNum("valuation_expectation")} testId="fp-val" />
              <Input label="Minimum Ticket Size (₹)" type="number" value={form.minimum_ticket_size || ""} onChange={setNum("minimum_ticket_size")} testId="fp-ticket" />
              <Input label="Existing Investors" value={form.existing_investors || ""} onChange={set("existing_investors")} testId="fp-existing" />
              <Input label="Previous Rounds" value={form.previous_rounds || ""} onChange={set("previous_rounds")} testId="fp-rounds" />
              <TextArea label="Cap Table Summary" value={form.cap_table_summary || ""} onChange={set("cap_table_summary")} testId="fp-cap" />
              <TextArea label="Use of Funds" value={form.use_of_funds || ""} onChange={set("use_of_funds")} testId="fp-use" />
              <Input label="Timeline for Raise" value={form.timeline_for_raise || ""} onChange={set("timeline_for_raise")} testId="fp-timeline" />
              <TextArea label="Competitive Advantage" value={form.competitive_advantage || ""} onChange={set("competitive_advantage")} testId="fp-adv" />
            </div>
          </section>

          <FundingRoundsSection />

          {isDebt && (
            <section className="border border-white/10 bg-white/70 backdrop-blur p-6 rounded-lg">
              <div className="overline mb-4">Debt-specific</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input label="Loan Amount" type="number" value={form.loan_amount || ""} onChange={setNum("loan_amount")} testId="fp-loan" />
                <Input label="Purpose of Loan" value={form.purpose_of_loan || ""} onChange={set("purpose_of_loan")} testId="fp-loan-purpose" />
                <Input label="Existing Debt" value={form.existing_debt || ""} onChange={set("existing_debt")} testId="fp-existing-debt" />
                <Input label="Collateral Available" value={form.collateral_available || ""} onChange={set("collateral_available")} testId="fp-coll" />
                <Input label="Banking History" value={form.banking_history || ""} onChange={set("banking_history")} testId="fp-bank" />
                <Input label="Repayment Capacity" value={form.repayment_capacity || ""} onChange={set("repayment_capacity")} testId="fp-repay" />
              </div>
            </section>
          )}

          <Button type="submit" data-testid="fp-save-btn">Save Funding Profile</Button>
        </form>

        <aside className="space-y-4 lg:sticky lg:top-6 h-fit">
          <PitchHealthMeter form={form} />
          <div className="rounded-lg border border-navy/10 bg-white p-4 text-xs text-ink-muted">
            <div className="overline mb-2 text-emerald-700">Auto-save on</div>
            Your work is saved every 20 seconds. You can leave and come back — nothing gets lost.
          </div>
        </aside>
      </div>
    </DashboardShell>
  );
}
